import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import translate from '../../i18n/translate';

/**
 * ValueLoop — interactive 4-stage rotating loop.
 *
 * The active stage is always anchored on the right, with its detail card
 * sliding in alongside. Clicking another cell (or "Next stage") rotates
 * the loop so the new active stage takes the right slot. Edges between
 * visited stages stay lit (soft, blurred) — building the picture of the
 * full cycle as the user explores.
 */

const RADIUS = 180;
const CELL_COUNT = 4;
const PARTICLE_PERIOD = 11000;
const TRAIL_LEN = 14;
const SVG_NS = 'http://www.w3.org/2000/svg';

// Stage data is just an array of indices [0..3]. All copy comes from i18n.
const STAGES = [0, 1, 2, 3];

// Position N maps to angle:  0=top -90°, 1=right 0°, 2=bottom 90°, 3=left 180°
function posForCell(cellIdx, activeIdx) {
  const positionIdx = (cellIdx - activeIdx + 1 + CELL_COUNT) % CELL_COUNT;
  const angleDeg = positionIdx * 90 - 90;
  const a = angleDeg * Math.PI / 180;
  return [Math.cos(a) * RADIUS, Math.sin(a) * RADIUS];
}

function getEdgePath(stageA, stageB, activeIdx) {
  const [ax, ay] = posForCell(stageA, activeIdx);
  const [bx, by] = posForCell(stageB, activeIdx);
  return `M ${ax.toFixed(2)} ${ay.toFixed(2)} A ${RADIUS} ${RADIUS} 0 0 1 ${bx.toFixed(2)} ${by.toFixed(2)}`;
}

export default function ValueLoop() {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [activeIdx, setActiveIdx] = useState(0);
  const [visited, setVisited] = useState(new Set([0]));
  // Edge paths/lit state mirrors the visited set but updates AFTER the cell rotation
  const [edgePaths, setEdgePaths] = useState(() =>
    STAGES.map(i => getEdgePath(i, (i + 1) % CELL_COUNT, 0))
  );
  const [edgesLit, setEdgesLit] = useState({});
  const [contentSwapKey, setContentSwapKey] = useState(0);

  const headRefs = [useRef(null), useRef(null)];
  const trailRefs = [useRef(null), useRef(null)];
  const ghostsRef = useRef([[], []]);
  const rafRef = useRef(null);
  const edgeTimerRef = useRef(null);

  // Build trail ghosts on mount
  useEffect(() => {
    [0, 1].forEach((pi) => {
      const trail = trailRefs[pi].current;
      if (!trail) return;
      const ghosts = [];
      for (let i = 0; i < TRAIL_LEN; i++) {
        const c = document.createElementNS(SVG_NS, 'circle');
        c.setAttribute('class', 'lv2-vl-ghost lv2-vl-ghost--' + (pi + 1));
        const tt = (i + 1) / (TRAIL_LEN + 1);
        const r = 5 * (1 - tt * 0.78);
        const o = (1 - tt) * 0.6;
        c.setAttribute('r', r.toFixed(2));
        c.setAttribute('opacity', o.toFixed(3));
        trail.appendChild(c);
        ghosts.push(c);
      }
      ghostsRef.current[pi] = ghosts;
    });
    return () => {
      [0, 1].forEach((pi) => {
        const trail = trailRefs[pi].current;
        if (trail) while (trail.firstChild) trail.removeChild(trail.firstChild);
      });
    };
  }, []);

  // Particle animation loop
  useEffect(() => {
    const t0 = performance.now();
    const histories = [[], []];
    const phases = [0, 0.5];

    const tick = (now) => {
      const elapsed = (now - t0) / PARTICLE_PERIOD;
      [0, 1].forEach((pi) => {
        const phase = (elapsed + phases[pi]) % 1;
        const angle = phase * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(angle) * RADIUS;
        const y = Math.sin(angle) * RADIUS;
        const head = headRefs[pi].current;
        if (head) {
          head.setAttribute('cx', x.toFixed(2));
          head.setAttribute('cy', y.toFixed(2));
        }
        histories[pi].unshift({ x, y });
        if (histories[pi].length > TRAIL_LEN + 1) histories[pi].pop();
        const ghosts = ghostsRef.current[pi];
        for (let i = 0; i < ghosts.length; i++) {
          const pos = histories[pi][i + 1];
          if (pos) {
            ghosts[i].setAttribute('cx', pos.x.toFixed(2));
            ghosts[i].setAttribute('cy', pos.y.toFixed(2));
          }
        }
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // When activeIdx changes: hide edges immediately, recompute after rotation
  useEffect(() => {
    setEdgesLit({}); // hide all edges (fade-out via CSS)
    if (edgeTimerRef.current) clearTimeout(edgeTimerRef.current);
    edgeTimerRef.current = setTimeout(() => {
      // Recompute paths for the new activeIdx and re-light visited edges
      const newPaths = STAGES.map(i => getEdgePath(i, (i + 1) % CELL_COUNT, activeIdx));
      const newLit = {};
      for (let i = 0; i < 4; i++) {
        const next = (i + 1) % CELL_COUNT;
        if (visited.has(i) && visited.has(next)) newLit[i] = true;
      }
      setEdgePaths(newPaths);
      setEdgesLit(newLit);
    }, 880);
    return () => {
      if (edgeTimerRef.current) clearTimeout(edgeTimerRef.current);
    };
  }, [activeIdx, visited]);

  const goTo = (idx) => {
    if (idx === activeIdx) return;
    setActiveIdx(idx);
    setVisited(prev => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    setContentSwapKey(k => k + 1);
  };

  const onNext = () => goTo((activeIdx + 1) % CELL_COUNT);
  const onReset = () => {
    setActiveIdx(0);
    setVisited(new Set([0]));
    setContentSwapKey(k => k + 1);
  };

  const isComplete = visited.size === 4;
  const stageNum = activeIdx + 1;

  return (
    <section className="lv2-section lv2-section--vloop" id="value-loop" style={{ paddingTop: 0 }}>
      <div className="lv2-shell">
        <div className="lv2-section-head" style={{ alignItems: 'center', textAlign: 'center', maxWidth: '100%' }}>
          <span className="lv2-section-tag">{t('lv2-vloop-tag')}</span>
          <h2 style={{ marginLeft: 'auto', marginRight: 'auto' }}>{t('lv2-vloop-h2')}</h2>
          <p className="sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>{t('lv2-vloop-sub')}</p>
          <div className="lv2-vl-progress">
            <span>{intl.formatMessage({ id: 'lv2-vloop-progress' }, { n: visited.size })}</span>
            <span className="lv2-vl-dots">
              {STAGES.map(i => (
                <span key={i} className={visited.has(i) ? 'lit' : ''} />
              ))}
            </span>
          </div>
        </div>

        <div className="lv2-vl-body">
          {/* LEFT: rotating loop */}
          <div className="lv2-vl-loop">
            <svg viewBox="-260 -260 520 520" xmlns="http://www.w3.org/2000/svg">
              <circle className="lv2-vl-ring" cx="0" cy="0" r={RADIUS} />

              {/* Edges */}
              {STAGES.map(i => (
                <path
                  key={i}
                  className={'lv2-vl-edge' + (edgesLit[i] ? ' lit' : '')}
                  d={edgePaths[i]}
                />
              ))}

              {/* Trails (ghosts injected via DOM) */}
              <g ref={trailRefs[1]} />
              <g ref={trailRefs[0]} />

              {/* Heads */}
              <circle ref={headRefs[0]} className="lv2-vl-head lv2-vl-head--1" r="5" cx="180" cy="0" />
              <circle ref={headRefs[1]} className="lv2-vl-head lv2-vl-head--2" r="5" cx="-180" cy="0" />

              {/* Cells */}
              {STAGES.map(i => {
                const [x, y] = posForCell(i, activeIdx);
                const cls = 'lv2-vl-cell'
                  + (i === activeIdx ? ' active' : '')
                  + (visited.has(i) && i !== activeIdx ? ' visited' : '');
                return (
                  <g key={i} className={cls} transform={`translate(${x.toFixed(2)},${y.toFixed(2)})`} onClick={() => goTo(i)}>
                    <circle className="outer" r="56" />
                    <circle className="bg"    r="44" />
                    <circle className="inner" r="32" />
                    <text className="num"   y="-6">{'0' + (i + 1)}</text>
                    <text className="label" y="14">{t('lv2-vloop-cell-' + (i + 1))}</text>
                  </g>
                );
              })}
            </svg>
            <span className="lv2-vl-pointer">→ {t('lv2-vloop-pointer')}</span>
          </div>

          {/* RIGHT: detail card */}
          <div className="lv2-vl-card-wrap">
            <article className="lv2-vl-card active-glow">
              <div className="lv2-vl-card-content" key={contentSwapKey}>
                <div className="lv2-vl-card-name">{t('lv2-vloop-' + stageNum + '-name')}</div>
                <h3 className="lv2-vl-card-h">{t('lv2-vloop-' + stageNum + '-h')}</h3>
                <p className="lv2-vl-card-p">{t('lv2-vloop-' + stageNum + '-p')}</p>
                <div className="lv2-vl-card-stats">
                  <div className="lv2-vl-stat">
                    <span className="label">{t('lv2-vloop-' + stageNum + '-sl1')}</span>
                    <span className="val">{t('lv2-vloop-' + stageNum + '-sv1')}</span>
                  </div>
                  <div className="lv2-vl-stat">
                    <span className="label">{t('lv2-vloop-' + stageNum + '-sl2')}</span>
                    <span className="val">{t('lv2-vloop-' + stageNum + '-sv2')}</span>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>

        <div className="lv2-vl-foot">
          <button type="button" className="lv2-vl-btn" onClick={onReset}>
            {t('lv2-vloop-reset')}
          </button>
          <button
            type="button"
            className={'lv2-vl-btn lv2-vl-btn-prim' + (isComplete ? '' : ' pulse')}
            onClick={isComplete ? onReset : onNext}
          >
            {isComplete
              ? t('lv2-vloop-replay')
              : <>{t('lv2-vloop-next')} <span style={{ fontFamily: 'var(--mono)' }}>→</span></>}
          </button>
        </div>
      </div>
    </section>
  );
}
